import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiUser, FiMail, FiBriefcase, FiTag } from 'react-icons/fi';
import api from '../services/api';
import ParticipantPhotoUpload from '../components/ParticipantPhotoUpload';

const EditParticipant = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        cpf: '',
        matricula: '',
        tipo: 'Interno',
        departamento: '',
        cargo: '',
        ativo: true
    });
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', msg: '' });
    const [validated, setValidated] = useState(false);

    const [biometryFile, setBiometryFile] = useState(null);
    const [participantPhotoUrl, setParticipantPhotoUrl] = useState(null);

    useEffect(() => {
        fetchParticipant();
    }, [id]);

    const fetchParticipant = async () => {
        try {
            const res = await api.get(`/participants/${id}`);
            setFormData({
                nome: res.data.nome,
                email: res.data.email,
                cpf: res.data.cpf,
                matricula: res.data.matricula,
                tipo: res.data.tipo,
                departamento: res.data.departamento,
                cargo: res.data.cargo,
                ativo: res.data.ativo
            });
            
            if (res.data.foto_url) {
                const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                // Adiciona timestamp para evitar cache
                setParticipantPhotoUrl(`${baseURL}${res.data.foto_url}?t=${new Date().getTime()}`);
            }
        } catch (err) {
            setFeedback({ type: 'danger', msg: 'Erro ao carregar dados do participante.' });
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        setValidated(true);
        setLoading(true);
        setFeedback({ type: '', msg: '' });

        try {
            await api.put(`/participants/${id}`, formData);

            if (biometryFile) {
                const formDataUpload = new FormData();
                formDataUpload.append('file', biometryFile);
                await api.post(`/participants/${id}/biometry`, formDataUpload);
            }

            setFeedback({ type: 'success', msg: 'Participante atualizado com sucesso! Redirecionando...' });
            setTimeout(() => navigate('/participant-management'), 2000);
        } catch (err) {
            const errorMsg = err.response?.data?.msg || 'Erro ao atualizar participante. Tente novamente.';
            setFeedback({ type: 'danger', msg: errorMsg });
            setLoading(false);
        }
    };

    return (
        <Container className="py-5" style={{ maxWidth: '800px' }}>
            <div className="mb-4 d-flex align-items-center justify-content-between">
                <div>
                    <Button variant="link" as={Link} to="/participant-management" className="text-decoration-none p-0 mb-1 text-muted">
                        <FiArrowLeft /> Voltar para Gestão
                    </Button>
                    <h2 className="fw-bold">Editar Participante</h2>
                    <p className="text-muted">Atualize os dados do participante abaixo.</p>
                </div>
            </div>

            <Card className="shadow-sm border-0 rounded-4">
                <Card.Body className="p-4 p-md-5">
                    {feedback.msg && <Alert variant={feedback.type} dismissible onClose={() => setFeedback({ type: '', msg: '' })}>{feedback.msg}</Alert>}
                    
                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <div className="d-flex justify-content-center mb-4">
                <ParticipantPhotoUpload 
                    onFileSelect={setBiometryFile}
                    initialPreviewUrl={participantPhotoUrl}
                    loading={loading}
                />
            </div>

            <Row className="g-4">
                            <Col md={12}>
                                <h5 className="mb-3 text-primary d-flex align-items-center gap-2">
                                    <FiUser /> Dados Pessoais
                                </h5>
                            </Col>
                            
                            <Col md={12}>
                                <Form.Group controlId="nome">
                                    <Form.Label>Nome Completo</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleChange}
                                        placeholder="Ex: Maria Oliveira"
                                        required 
                                        className="py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">Por favor, informe o nome completo.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group controlId="email">
                                    <Form.Label>Email</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0"><FiMail /></span>
                                        <Form.Control 
                                        type="email" 
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="exemplo@email.com"
                                        className="py-2 border-start-0"
                                    />
                                    <Form.Control.Feedback type="invalid">Por favor, informe um email válido.</Form.Control.Feedback>
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="cpf">
                                    <Form.Label>CPF</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="cpf"
                                        value={formData.cpf}
                                        onChange={handleChange}
                                        placeholder="000.000.000-00"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <hr className="my-2" />
                            </Col>

                            <Col md={12}>
                                <h5 className="mb-3 text-primary d-flex align-items-center gap-2">
                                    <FiBriefcase /> Dados Profissionais
                                </h5>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="tipo">
                                    <Form.Label>Tipo</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0"><FiTag /></span>
                                        <Form.Select 
                                            name="tipo"
                                            value={formData.tipo}
                                            onChange={handleChange}
                                            className="py-2 border-start-0"
                                        >
                                            <option value="Interno">Interno</option>
                                            <option value="Externo">Externo</option>
                                        </Form.Select>
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="matricula">
                                    <Form.Label>Matrícula (Opcional)</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="matricula"
                                        value={formData.matricula}
                                        onChange={handleChange}
                                        placeholder="Ex: 12345"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="departamento">
                                    <Form.Label>Departamento</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="departamento"
                                        value={formData.departamento}
                                        onChange={handleChange}
                                        placeholder="Ex: Recursos Humanos"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="cargo">
                                    <Form.Label>Cargo</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="cargo"
                                        value={formData.cargo}
                                        onChange={handleChange}
                                        placeholder="Ex: Analista"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group controlId="ativo">
                                    <Form.Check 
                                        type="switch"
                                        id="custom-switch"
                                        label="Participante Ativo"
                                        name="ativo"
                                        checked={formData.ativo}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={12} className="mt-4 d-flex justify-content-end gap-3">
                                <Button variant="outline-secondary" as={Link} to="/participant-management" className="px-4">
                                    Cancelar
                                </Button>
                                <Button variant="primary" type="submit" disabled={loading} className="px-4 d-flex align-items-center gap-2">
                                    {loading ? <Spinner animation="border" size="sm" /> : <FiSave />}
                                    Salvar Alterações
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default EditParticipant;
